import React from 'react';
import { DollarSign, Package, TrendingUp, TrendingDown, Calendar, CheckCircle } from 'lucide-react';

export default function PerformanceKPICards({ data, statistics }) {
  if (!data) return null;

  const kpis = [
    {
      title: 'Valeur Stock Total',
      value: `€${data?.totalStockValue?.toFixed(2)}`,
      icon: DollarSign,
      color: 'green',
      trend: '+5.2%',
      trendUp: true
    },
    {
      title: 'Consommation Mensuelle',
      value: `${(data?.monthlyConsumption?.noir_270l + data?.monthlyConsumption?.bleu_220l)?.toFixed(0)} unités`,
      icon: Package,
      color: 'blue',
      subtitle: `Noir: ${data?.monthlyConsumption?.noir_270l?.toFixed(0)} | Bleu: ${data?.monthlyConsumption?.bleu_220l?.toFixed(0)}`
    },
    {
      title: 'Jours de Stock Moyen',
      value: `${Math.round((data?.daysOfSupply?.noir_270l + data?.daysOfSupply?.bleu_220l) / 2)} jours`,
      icon: Calendar,
      color: 'purple',
      subtitle: `Noir: ${Math.round(data?.daysOfSupply?.noir_270l)}j | Bleu: ${Math.round(data?.daysOfSupply?.bleu_220l)}j`,
      alert: Math.min(data?.daysOfSupply?.noir_270l, data?.daysOfSupply?.bleu_220l) < 14
    },
    {
      title: 'Valeur Commande Moyenne',
      value: `€${data?.avgOrderValue?.toFixed(2)}`,
      icon: DollarSign,
      color: 'yellow',
      trend: '+3.8%',
      trendUp: true
    },
    {
      title: 'Taux de Livraison',
      value: `${data?.deliveryRate?.toFixed(1)}%`,
      icon: CheckCircle,
      color: 'green',
      trend: data?.deliveryRate >= 90 ? 'Excellent' : data?.deliveryRate >= 75 ? 'Bon' : 'À améliorer'
    },
    {
      title: 'Taux de Collecte',
      value: `${data?.collectionRate?.toFixed(1)}%`,
      icon: TrendingUp,
      color: 'blue',
      trend: data?.collectionRate >= 80 ? 'Excellent' : data?.collectionRate >= 60 ? 'Bon' : 'À améliorer',
      alert: data?.collectionRate < 70
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      green: 'bg-green-50 text-green-600',
      blue: 'bg-blue-50 text-blue-600',
      purple: 'bg-purple-50 text-purple-600',
      yellow: 'bg-yellow-50 text-yellow-600',
      red: 'bg-red-50 text-red-600'
    };
    return colors?.[color] || colors?.green;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
      {kpis?.map((kpi, index) => (
        <div
          key={index}
          className={`bg-white rounded-lg shadow-sm border p-6 transition-all duration-300 hover:shadow-md ${
            kpi?.alert ? 'border-red-300 bg-red-50/30' : 'border-gray-200'
          }`}
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">{kpi?.title}</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{kpi?.value}</p>
              {kpi?.subtitle && (
                <p className="text-xs text-gray-500 mt-1">{kpi?.subtitle}</p>
              )}
            </div>
            <div className={`p-3 rounded-lg ${getColorClasses(kpi?.color)}`}>
              <kpi.icon className="w-6 h-6" />
            </div>
          </div>
          
          {kpi?.trend && (
            <div className="flex items-center gap-1 text-sm">
              {kpi?.trendUp !== undefined && (
                kpi?.trendUp ? (
                  <TrendingUp className="w-4 h-4 text-green-600" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-600" />
                )
              )}
              <span className={kpi?.trendUp ? 'text-green-600 font-medium' : 'text-gray-600'}>
                {kpi?.trend}
              </span>
            </div>
          )}
          
          {kpi?.alert && (
            <div className="mt-2 text-xs text-red-600 font-medium">
              ⚠️ Attention requise
            </div>
          )}
        </div>
      ))}
    </div>
  );
}